import { useEffect, useState, RefObject } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { confirmDialog } from "primereact/confirmdialog";
import { QueryObserverResult } from "@tanstack/react-query";
import { endOfMonth } from "date-fns";

import { useAuthStore } from "presentation/stores/use-auth-store";
import { useCreateVacationPlanMutation } from "presentation/hooks/use-query-vacation";
import { Vacation } from "domain/entities/vacations";
import { Message } from "domain/entities/message";
import { Parameter } from "domain/entities/parameter";
import MobileInputDateComponent from "presentation/mobile/components/MobileInputDateComponent";
import MobileBottomSheet from "presentation/mobile/components/MobileBottomSheet";

interface ScheduleVacationModalProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    toastRef?: RefObject<Toast | null>;
    refetch: () => Promise<QueryObserverResult<Message<Vacation[]> | undefined, Error>>;
    refetchEmployeeData?: () => Promise<any>;
    vacationDaysCurrentYear: number;
    vacationDaysLastYear?: number | null;
    vacationDaysNextYear?: number | null;
    vacationDaysNextYearFromLastYear?: number | null;
    maxVacationDaysNextYear?: Parameter[];
}

interface BalanceTileProps {
    value: number;
    label: string;
    caption: string;
    deadline?: string;
}

const BalanceTile: React.FC<BalanceTileProps> = ({ value, label, caption, deadline }) => {
    const hasDays = value > 0;

    return (
        <div
            className="flex-1 flex flex-column align-items-center justify-content-center text-center py-2 px-2"
            style={{ backgroundColor: hasDays ? "#EFF6FF" : "#FFFFFF" }}
        >
            <span
                style={{
                    fontSize: "1.35rem",
                    fontWeight: 700,
                    lineHeight: 1,
                    color: hasDays ? "#0084D1" : "#0F172A",
                }}
            >
                {value}
            </span>
            <span className="text-xs font-semibold mt-1" style={{ color: "#0F172A" }}>
                {label}
            </span>
            {deadline ? (
                <span
                    className="inline-flex align-items-center gap-1 mt-1 px-2 text-xs font-bold"
                    style={{
                        color: "#0084D1",
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #BFDBFE",
                        borderRadius: "999px",
                        paddingTop: "1px",
                        paddingBottom: "1px",
                    }}
                >
                    <i className="pi pi-exclamation-circle" style={{ fontSize: "0.65rem" }} />
                    {deadline}
                </span>
            ) : (
                <span className="text-xs mt-1" style={{ color: "#64748B" }}>
                    {caption}
                </span>
            )}
        </div>
    );
};

const ScheduleVacationModal: React.FC<ScheduleVacationModalProps> = ({

    visible,
    setVisible,
    toastRef,
    refetch,
    refetchEmployeeData,
    vacationDaysCurrentYear,
    vacationDaysLastYear,
    vacationDaysNextYear,

    vacationDaysNextYearFromLastYear,
    maxVacationDaysNextYear,
}) => {
    const { userEmployee } = useAuthStore();
    const employeeId = userEmployee?.id;

    const [form, setForm] = useState<{
        startDate: Date | null;
        endDate: Date | null;
    }>({
        startDate: null,
        endDate: null,
    });

    const { mutateAsync: createVacation, isPending: isCreating } =
        useCreateVacationPlanMutation();

    // Reset ao fechar
    useEffect(() => {
        if (!visible) {
            setForm({ startDate: null, endDate: null });
        }
    }, [visible]);


    // Limites de datas alinhados com as regras da empresa
    const currentDate = new Date();
    const startDateMin =
        currentDate.getMonth() === 0
            ? new Date(currentDate.getFullYear() - 1, 11, 1)
            : new Date(currentDate.getFullYear(), 0, 1);
    const startDateMax = new Date(currentDate.getFullYear(), 11, 31);

    const endDateMin = form.startDate ?? new Date();
    const endDateMax = form.startDate
        ? new Date(form.startDate.getFullYear(), 11, 31)
        : undefined;

    // Regra: auto-preencher endDate garantindo que não ultrapassa 31 de dezembro do ano de início
    useEffect(() => {
        if (!form.startDate) return;

        const maxDate = new Date(form.startDate.getFullYear(), 11, 31);

        if (
            !form.endDate ||
            form.endDate < form.startDate ||
            form.endDate > maxDate
        ) {
            setForm((prev) => ({ ...prev, endDate: form.startDate }));
        }
    }, [form.startDate]);

    const handleSchedule = async (isToUseNextYearDays: boolean = false) => {
        if (!employeeId || !form.startDate || !form.endDate) return;

        const payload = {
            startAt: form.startDate,
            endAt: form.endDate,
            employee: { id: employeeId },
            isToUseNextYearDays,
        };

        try {
            await createVacation(payload as any);
            await refetchEmployeeData?.();
            toastRef?.current?.show({
                severity: "success",
                summary: "Agendamento",
                detail: "Férias agendadas com sucesso!",
                life: 3500,
            });
            await refetch();
            setVisible(false);
        } catch (error: any) {
            const regexVacationExists =
                /^Employee with id [0-9a-fA-F-]{36} has already VacationPlan\(s\) for some days requested\.$/;
            const regexNotEnoughDays =
                /^Employee with id [0-9a-fA-F-]{36} has not enough vacation days available\.$/;
            const regexInvalidDate =
                /^VacationPlan for employee with id [0-9a-fA-F-]{36} has not valid days\.$/;

            const errorMessage: string =
                error?.response?.data?.metadata?.errors?.[0] ??
                error?.message ??
                "";

            const nextYearToConsider = new Date(form.startDate).getFullYear() + 1;
            const vacationDaysNextYearToConsider =
                new Date(form.startDate).getFullYear() < new Date().getFullYear()
                    ? vacationDaysNextYearFromLastYear
                    : vacationDaysNextYear;

            // Se dias insuficientes a partir de julho e ainda não optou por usar dias do próximo ano:
            if (
                regexNotEnoughDays.test(errorMessage) &&
                !isToUseNextYearDays &&
                new Date(form.startDate).getMonth() + 1 >= 7
            ) {
                const maxAllowed = Number(maxVacationDaysNextYear?.at(0)?.value ?? 0);
                const alreadyAnticipated = (vacationDaysNextYearToConsider ?? 0) * -1;

                confirmDialog({
                    message: (
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                            <i
                                className="pi pi-exclamation-triangle"
                                style={{
                                    fontSize: "1.4rem",
                                    flexShrink: 0,
                                    marginTop: "2px",
                                    color: "#D97706",
                                }}
                            />
                            <div style={{ lineHeight: "1.5" }}>
                                <div className="text-sm font-medium" style={{ color: "#1E293B" }}>
                                    Não existem dias de férias suficientes disponíveis. Deseja antecipar
                                    férias do próximo ano civil ({nextYearToConsider})?
                                </div>
                                <div className="text-xs text-600 mt-2">
                                    Já antecipou {alreadyAnticipated} dia
                                    {alreadyAnticipated !== 1 ? "s" : ""} do ano {nextYearToConsider}, num
                                    máximo possível de {maxAllowed} dia
                                    {maxAllowed !== 1 ? "s" : ""}.
                                </div>
                            </div>
                        </div>
                    ),
                    header: "Dias de férias insuficientes",
                    acceptLabel: "Sim",
                    rejectLabel: "Não",
                    accept: () => {
                        handleSchedule(true);
                    },
                });
                return;
            }

            const msg = regexVacationExists.test(errorMessage)
                ? "Já existe um agendamento de férias para o período selecionado. Por favor, escolha outro período ou cancele o agendamento existente."
                : regexInvalidDate.test(errorMessage)
                    ? "Não é possível agendar férias no período solicitado."
                    : regexNotEnoughDays.test(errorMessage)
                        ? "Não existem dias de férias suficientes disponíveis."
                        : "Erro ao agendar férias.";

            toastRef?.current?.show({
                severity: "error",
                summary: "Erro",
                detail: msg,
                life: 5000,
            });
        }
    };

    const footer = (
        <div
            className="w-full flex gap-2"
            style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom, 0px))" }}
        >
            <Button
                label="Cancelar"
                className="btn-mobile-secondary flex-1"
                type="button"
                onClick={() => setVisible(false)}
            />
            <Button
                label="Agendar Férias"
                className="btn-mobile flex-1"
                type="button"
                onClick={() => handleSchedule(false)}
                disabled={isCreating || !form.startDate || !form.endDate}
                loading={isCreating}
            />
        </div>
    );

    const anticipatedDays = Math.abs(vacationDaysNextYear ?? 0);
    const lastYearDays = vacationDaysLastYear ?? 0;
    const currentYear = new Date().getFullYear();

    return (
        <MobileBottomSheet
            visible={visible}
            onHide={() => setVisible(false)}
            title="Agendar Férias"
            headerIcon="pi pi-calendar"
            footer={footer}
        >
            <form className="w-full flex flex-column gap-3 py-2">
                <div className="w-full">
                    <span
                        className="block text-xs font-semibold mb-2"
                        style={{ color: "#64748B" }}
                    >
                        Dias disponíveis
                    </span>
                    <div
                        className="w-full border-round-lg overflow-hidden"
                        style={{ border: "1px solid #E2E8F0" }}
                    >
                        <div className="flex">
                            <BalanceTile
                                value={vacationDaysCurrentYear}
                                label="Ano atual"
                                caption={`Dias de ${currentYear}`}
                            />
                            <div style={{ width: "1px", backgroundColor: "#E2E8F0" }} />
                            <BalanceTile
                                value={lastYearDays}
                                label="Ano anterior"
                                caption={`Dias de ${currentYear - 1}`}
                                deadline={lastYearDays > 0 ? "Usar até 30/04" : undefined}
                            />
                        </div>

                        {anticipatedDays > 0 && (
                            <div
                                className="flex align-items-center justify-content-center gap-2 py-2 px-3"
                                style={{ borderTop: "1px solid #DBEAFE", backgroundColor: "#EFF6FF" }}
                            >
                                <i className="pi pi-info-circle text-xs" style={{ color: "#0084D1" }} />
                                <span className="text-xs font-medium" style={{ color: "#334155" }}>
                                    {anticipatedDays} {anticipatedDays === 1 ? "dia antecipado" : "dias antecipados"} de {currentYear + 1}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Data de Início */}
                <div className="w-full">
                    <label htmlFor="startDate" className="text-xs font-semibold text-700 mb-1 block">
                        Data de Início
                    </label>
                    <MobileInputDateComponent
                        fieldName="startDate"
                        placeholder="Selecionar data de início"
                        dateFormat="dd/mm/yy"
                        currentObject={form}
                        getValue={form.startDate}
                        setValue={(updated: any) =>
                            setForm((prev) => ({
                                ...prev,
                                startDate: updated.startDate
                                    ? new Date(updated.startDate)
                                    : null,
                            }))
                        }
                        minDate={startDateMin}
                        maxDate={startDateMax}
                    />
                </div>

                {/* Data de Fim */}
                <div className="w-full">
                    <label htmlFor="endDate" className="text-xs font-semibold text-700 mb-1 block">
                        Data de Fim
                    </label>
                    <MobileInputDateComponent
                        fieldName="endDate"
                        placeholder="Selecionar data de fim"
                        dateFormat="dd/mm/yy"
                        currentObject={form}
                        getValue={form.endDate}
                        setValue={(updated: any) =>
                            setForm((prev) => ({
                                ...prev,
                                endDate: updated.endDate
                                    ? new Date(updated.endDate)
                                    : null,
                            }))
                        }
                        minDate={endDateMin}
                        maxDate={endDateMax}
                    />
                </div>

            </form>
        </MobileBottomSheet >
    );
};

export default ScheduleVacationModal;